import React from 'react';
import moment from 'moment';
import { CloseCircleOutlined, DeleteOutlined, InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Avatar, Button, Col, Form, Input, InputNumber, Row, Select, TimePicker, Tooltip } from 'antd';
import { FormListFieldData } from 'antd/lib/form/FormList';
import { NamePath } from 'antd/lib/form/interface';
import TransportType from '@packages/routing/enums/transportTypes';
import PackageSize from '../../types/Package';
import { AutoStackingTimeSlot } from '../../types/Feature';

interface AutoStackingTimeSlotsInterfaceProps {
    isNotDetailMode: boolean;
    isFeatureDisabled: boolean;
    enableControls: boolean | undefined;
    field: FormListFieldData;
    timeSlots?: AutoStackingTimeSlot[];
    removeHandler: (fieldName: number) => void;
}

const AutoStackingTimeSlots: React.FC<AutoStackingTimeSlotsInterfaceProps> = ({
    isNotDetailMode,
    isFeatureDisabled,
    enableControls,
    field,
    timeSlots,
    removeHandler,
}) => {
    const packageSizeOptions = Object.keys(PackageSize).map((packageSize) => (
        <Select.Option key={packageSize} value={packageSize}>
            {packageSize}
        </Select.Option>
    ));

    const transportTypeOptions = Object.values(TransportType)
        .sort()
        .map((transportType) => (
            <Select.Option key={transportType} value={transportType}>
                {transportType}
            </Select.Option>
        ));

    const weekDays = [
        { key: 1, value: 'Mondays' },
        { key: 2, value: 'Tuesdays' },
        { key: 3, value: 'Wednesdays' },
        { key: 4, value: 'Thursdays' },
        { key: 5, value: 'Fridays' },
        { key: 6, value: 'Saturdays' },
        { key: 7, value: 'Sundays' },
    ];

    const areTimesValid = (
        getFieldValue: (path: NamePath) => any,
        date: string,
        fieldName: number,
        isInitDate: boolean,
    ): boolean => {
        if (!date) return true;
        const startTime = isInitDate ? date : getFieldValue(['timeSlots', fieldName, 'startTime']);
        const endTime = isInitDate ? getFieldValue(['timeSlots', fieldName, 'endTime']) : date;

        if ((isInitDate && !endTime) || (!isInitDate && !startTime)) return true;
        const [iHour, iMinute] = moment(startTime).utc().format('H:m').split(':');
        const [eHour, eMinute] = moment(endTime).utc().format('H:m').split(':');
        return +iHour * 100 + +iMinute < +eHour * 100 + +eMinute;
    };

    return (
        <Col lg={{ span: 21, offset: 1 }} className="border mt-4" style={{ padding: 15, borderRadius: 15 }}>
            <Row gutter={24}>
                <Col lg={{ span: 6 }} md={{ span: 12 }} sm={{ span: 23 }} xs={{ span: 23 }}>
                    <Form.Item
                        name={[field.name, 'dayOfWeek']}
                        label={
                            // eslint-disable-next-line react/jsx-wrap-multilines
                            <>
                                <span className="mr-2">Day of week</span>
                                <Tooltip title="Units in seconds">
                                    <InfoCircleOutlined className="text-muted" />
                                </Tooltip>
                            </>
                        }
                        rules={
                            /* eslint-disable indent */
                            isNotDetailMode
                                ? [
                                      {
                                          required: !isFeatureDisabled,
                                          message: 'dayOfWeek is required',
                                      },
                                  ]
                                : []
                            /* eslint-disable indent */
                        }
                    >
                        {!isNotDetailMode ? (
                            <span className="ant-form-text text-secondary">
                                {timeSlots && timeSlots[field.name] !== null
                                    ? weekDays.find((day) => day.key === timeSlots[field.name].dayOfWeek)?.value
                                    : '-'}
                            </span>
                        ) : (
                            <Select
                                placeholder="Select one or more package types"
                                disabled={isFeatureDisabled || !enableControls}
                                allowClear
                                showArrow
                            >
                                {weekDays.map((day) => (
                                    <Select.Option key={day.key} value={day.key}>
                                        {day.value}
                                    </Select.Option>
                                ))}
                            </Select>
                        )}
                    </Form.Item>
                </Col>
                <Col lg={{ span: 4 }} md={{ span: 6 }} sm={{ span: 12 }} xs={{ span: 23 }}>
                    <Form.Item
                        name={[field.name, 'startTime']}
                        dependencies={[`timeSlots.${field.name}'.endTime`]}
                        rules={
                            /* eslint-disable indent */
                            isNotDetailMode
                                ? [
                                      {
                                          required: !isFeatureDisabled,
                                          message: 'End Time field is required',
                                      },
                                      ({ getFieldValue }) => ({
                                          validator(_, value, callback): void {
                                              if (!areTimesValid(getFieldValue, value, field.name, true))
                                                  return callback('Start time should be less than End time');

                                              return callback();
                                          },
                                      }),
                                  ]
                                : []
                            /* eslint-disable indent */
                        }
                        label="Start time"
                    >
                        {!isNotDetailMode ? (
                            <span className="ant-form-text text-secondary">
                                {timeSlots && timeSlots[field.name]
                                    ? moment(
                                          `${moment().format('YYYY-MM-DD')}T${timeSlots[field.name].startTime}`,
                                      ).format('HH:mm')
                                    : '-'}
                            </span>
                        ) : (
                            <TimePicker format="HH:mm" />
                        )}
                    </Form.Item>
                </Col>
                <Col lg={{ span: 4 }} md={{ span: 6 }} sm={{ span: 12 }} xs={{ span: 23 }}>
                    <Form.Item
                        name={[field.name, 'endTime']}
                        dependencies={['startTime']}
                        rules={
                            /* eslint-disable indent */
                            isNotDetailMode
                                ? [
                                      {
                                          required: !isFeatureDisabled,
                                          message: 'dayOfWeek is required',
                                      },
                                      ({ getFieldValue }) => ({
                                          validator(_, value, callback): void {
                                              if (!areTimesValid(getFieldValue, value, field.name, false))
                                                  return callback('End time should be greather than Start time');

                                              return callback();
                                          },
                                      }),
                                  ]
                                : []
                            /* eslint-disable indent */
                        }
                        label="End time"
                    >
                        {!isNotDetailMode ? (
                            <span className="ant-form-text text-secondary">
                                {timeSlots && timeSlots[field.name]
                                    ? moment(
                                          `${moment().format('YYYY-MM-DD')}T${timeSlots[field.name].endTime}`,
                                      ).format('HH:mm')
                                    : '-'}
                            </span>
                        ) : (
                            <TimePicker format="HH:mm" />
                        )}
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={24}>
                <Col lg={{ span: 6 }} md={{ span: 23 }} sm={{ span: 23 }} xs={{ span: 23 }}>
                    <Form.Item
                        name={[field.name, 'minTimeUntilPickup']}
                        label={
                            // eslint-disable-next-line react/jsx-wrap-multilines
                            <>
                                <span className="mr-2">Minimum time until pick-up (sec.)</span>
                                <Tooltip title="Units in seconds">
                                    <InfoCircleOutlined className="text-muted" />
                                </Tooltip>
                            </>
                        }
                        rules={
                            /* eslint-disable indent */
                            isNotDetailMode
                                ? [
                                      {
                                          required: !isFeatureDisabled,
                                          message: 'Field Minimum time until pick-up (sec.) is required',
                                      },
                                  ]
                                : []
                            /* eslint-enable indent */
                        }
                    >
                        {!isNotDetailMode ? (
                            <span className="ant-form-text text-secondary">
                                {(timeSlots && timeSlots[field.name].minTimeUntilPickup) || '-'}
                            </span>
                        ) : (
                            <InputNumber
                                name="minTimeUntilPickup"
                                // id="ft-config__min-time-until-pickup"
                                title="Minimum time until pick-up (sec.)"
                                disabled={isFeatureDisabled || !enableControls}
                                min={0}
                                step={1}
                                autoComplete="off"
                            />
                        )}
                    </Form.Item>
                </Col>
                <Col lg={{ span: 4 }} md={{ span: 12 }} sm={{ span: 12 }} xs={{ span: 23 }}>
                    <Form.Item
                        name={[field.name, 'maxStackSize']}
                        /* eslint-disable prettier/prettier */
                        label={
                            // eslint-disable-next-line react/jsx-wrap-multilines
                            <>
                                <span className="mr-1">Stack size</span>
                                <Tooltip title="Maximum stack size">
                                    <InfoCircleOutlined className="text-muted" />
                                </Tooltip>
                            </>
                        }
                        rules={
                            /* eslint-disable indent */
                            isNotDetailMode
                                ? [
                                      {
                                          required: !isFeatureDisabled,
                                          message: 'Field Maximum stack size is required',
                                      },
                                  ]
                                : []
                            /* eslint-enable indent */
                        }
                        /* eslint-enable prettier/prettier */
                    >
                        {!isNotDetailMode ? (
                            <span className="ant-form-text text-secondary">
                                {(timeSlots && timeSlots[field.name].maxStackSize) || '-'}
                            </span>
                        ) : (
                            <InputNumber
                                name="maxStackSize"
                                title="Maximum stack size"
                                min={0}
                                step={1}
                                autoComplete="off"
                                disabled={isFeatureDisabled || !enableControls}
                            />
                        )}
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={24}>
                <Col lg={{ span: 8 }} md={{ span: 23 }} sm={{ span: 23 }} xs={{ span: 23 }}>
                    <Form.Item
                        name={[field.name, 'transportType']}
                        initialValue={(timeSlots && timeSlots[field.name]?.transportType) || TransportType.MOTORBIKE}
                        label={
                            // eslint-disable-next-line react/jsx-wrap-multilines
                            <>
                                <span className="mr-2">Transport Type</span>
                                <Tooltip title="Used for route resolution">
                                    <InfoCircleOutlined className="text-muted" />
                                </Tooltip>
                            </>
                        }
                        rules={
                            /* eslint-disable indent */
                            isNotDetailMode
                                ? [
                                      {
                                          required: !isFeatureDisabled,
                                          message: 'Field Transport Type is required',
                                      },
                                  ]
                                : []
                            /* eslint-enable indent */
                        }
                    >
                        {!isNotDetailMode ? (
                            <span className="ant-form-text text-secondary">
                                {(timeSlots && timeSlots[field.name].transportType) || 'motorbike'}
                            </span>
                        ) : (
                            <Select
                                className="w-100"
                                placeholder="Select one transport type"
                                disabled={isFeatureDisabled || !enableControls}
                                showArrow
                            >
                                {transportTypeOptions}
                            </Select>
                        )}
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={24} className="h6 text-secondary mb-3">
                <Col lg={24}>Optional configuration:</Col>
            </Row>
            <Row gutter={24}>
                <Col lg={{ span: 5 }} md={{ span: 10 }} sm={{ span: 23 }} xs={{ span: 23 }}>
                    <Form.Item
                        name={[field.name, 'maxDrivenDistance']}
                        label={
                            // eslint-disable-next-line react/jsx-wrap-multilines
                            <>
                                <span className="mr-1">Maximum driven distance</span>
                                <Tooltip title="Units in kilometers">
                                    <InfoCircleOutlined className="text-muted" />
                                </Tooltip>
                            </>
                        }
                    >
                        {!isNotDetailMode ? (
                            <span className="ant-form-text text-secondary">
                                {(timeSlots && timeSlots[field.name].maxDrivenDistance) || '-'}
                            </span>
                        ) : (
                            <InputNumber
                                name="maxDrivenDistance"
                                title="Maximum driven distance (km.)"
                                min={0}
                                step={1}
                                autoComplete="off"
                                disabled={isFeatureDisabled || !enableControls}
                            />
                        )}
                    </Form.Item>
                </Col>
                <Col lg={{ span: 4 }} md={{ span: 10 }} sm={{ span: 23 }} xs={{ span: 23 }}>
                    <Form.Item
                        name={[field.name, 'maxTotalTime']}
                        label={
                            // eslint-disable-next-line react/jsx-wrap-multilines
                            <>
                                <span className="mr-1">Maximum total time</span>
                                <Tooltip title="Units in minutes">
                                    <InfoCircleOutlined className="text-muted" />
                                </Tooltip>
                            </>
                        }
                    >
                        {!isNotDetailMode ? (
                            <span className="ant-form-text text-secondary">
                                {(timeSlots && timeSlots[field.name].maxTotalTime) || '-'}
                            </span>
                        ) : (
                            <InputNumber
                                name="maxTotalTime"
                                title="Maximum total time (min.)"
                                min={0}
                                step={1}
                                autoComplete="off"
                                disabled={isFeatureDisabled || !enableControls}
                            />
                        )}
                    </Form.Item>
                </Col>
                <Col
                    lg={{ span: 12 }}
                    md={{ span: 24 }}
                    sm={{ span: 23 }}
                    xs={{ span: 23 }}
                    title="Package type filter"
                >
                    <Form.Item
                        name={[field.name, 'packageTypeFilter']}
                        label={
                            // eslint-disable-next-line react/jsx-wrap-multilines
                            <>
                                <span className="mr-1">Package type filter</span>
                                <Tooltip title="Only packages with these types will be considered">
                                    <InfoCircleOutlined className="text-muted" />
                                </Tooltip>
                            </>
                        }
                    >
                        {!isNotDetailMode ? (
                            <span>-</span>
                        ) : (
                            <Select
                                mode="multiple"
                                className="w-100"
                                placeholder="Select one or more package types"
                                disabled={isFeatureDisabled || !enableControls}
                                allowClear
                                showArrow
                            >
                                {packageSizeOptions}
                            </Select>
                        )}
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={24}>
                <Col span={21} xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 21 }}>
                    <p className="mb-3">Pick-up address filters:</p>
                    <p className="text-secondary">Only packages with these pick-up addresses will be considered.</p>
                    {!isNotDetailMode ? (
                        <span>-</span>
                    ) : (
                        <Form.List name={[field.name, 'pickupAddressFilter']}>
                            {(fields, { add, remove }): JSX.Element => (
                                <>
                                    {fields.map((pickupField, index) => (
                                        <Row gutter={24} key={pickupField.key} className="mb-3">
                                            <Col span={20} xs={{ span: 19 }} sm={{ span: 19 }} md={{ span: 20 }}>
                                                <Form.Item
                                                    name={[pickupField.name, 'pickupAddress']}
                                                    label={
                                                        // eslint-disable-next-line react/jsx-wrap-multilines
                                                        <>
                                                            <Avatar
                                                                className={`${
                                                                    isFeatureDisabled || !enableControls
                                                                        ? ''
                                                                        : 'bg-primary'
                                                                } mr-2`}
                                                                size="small"
                                                            >
                                                                {index + 1}
                                                            </Avatar>
                                                            Pick-up address
                                                        </>
                                                    }
                                                    /* eslint-enable prettier/prettier */
                                                >
                                                    <Input
                                                        title="Pick-up address"
                                                        placeholder="Enter pick-up address"
                                                        disabled={isFeatureDisabled || !enableControls}
                                                        autoComplete="off"
                                                    />
                                                </Form.Item>
                                            </Col>
                                            <Col
                                                span={4}
                                                xs={{ span: 1 }}
                                                sm={{ span: 1 }}
                                                md={{ span: 4 }}
                                                className="d-flex align-items-center justify-content-start"
                                            >
                                                <Form.Item name={[field.name, 'remove-button']} className="m-0">
                                                    <Button
                                                        danger
                                                        type="link"
                                                        title="Remove"
                                                        className="align-items-center d-flex"
                                                        tabIndex={-1}
                                                        icon={<CloseCircleOutlined />}
                                                        disabled={isFeatureDisabled || !enableControls}
                                                        onClick={(): void => remove(field.name)}
                                                    >
                                                        <span className="d-none d-sm-block">Remove</span>
                                                    </Button>
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    ))}
                                    <Button
                                        type="dashed"
                                        title="Add pick-up address"
                                        name="btn-add-pickup-address"
                                        id="btn-add-pickup-address"
                                        className={`${
                                            isFeatureDisabled || !enableControls ? '' : 'text-primary'
                                        } d-flex align-items-center justify-content-center w-100 mt-4`}
                                        disabled={isFeatureDisabled || !enableControls}
                                        icon={<PlusOutlined />}
                                        onClick={(): void => {
                                            add();
                                        }}
                                    >
                                        Add pick-up address
                                    </Button>
                                </>
                            )}
                        </Form.List>
                    )}
                </Col>
            </Row>
            {isNotDetailMode && (
                <Row gutter={24}>
                    <Col lg={24}>
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            title="Delete"
                            style={{ float: 'right' }}
                            onClick={(): void => removeHandler(field.name)}
                            className="d-flex align-items-center justify-content-center "
                        >
                            Delete
                        </Button>
                    </Col>
                </Row>
            )}
        </Col>
    );
};

export default AutoStackingTimeSlots;
